import React from 'react';
import { AlertTriangle, CheckCircle, Clock, Edit3, Globe, Loader2, Lock, Route, Save, Trash2, X } from 'lucide-react';
import { GpxRoute, RouteDifficulty } from '../../../types';

type RouteEditFields = {
  name: string;
  description: string;
  difficulty: RouteDifficulty;
};

type AdminRouteCardProps = {
  route: GpxRoute;
  isPendingMode: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  editFields: RouteEditFields;
  processingId: string | null;
  difficultyIcons: Record<RouteDifficulty, React.ReactNode>;
  renderRouteMap: (route: GpxRoute) => React.ReactNode;
  onEditFieldChange: (field: keyof RouteEditFields, value: string) => void;
  onSaveRouteUpdate: (id: string) => Promise<void>;
  onCancelEdit: () => void;
  onApproveRoute: (route: GpxRoute, approved: boolean) => Promise<void>;
  onStartEditRoute: (route: GpxRoute) => void;
  onRequestDelete: (id: string) => void;
  onConfirmDelete: (id: string) => Promise<void>;
  onCancelDelete: () => void;
  onMakePublic?: (route: GpxRoute) => Promise<void>;
};

const AdminRouteCard: React.FC<AdminRouteCardProps> = ({
  route,
  isPendingMode,
  isEditing,
  isDeleting,
  editFields,
  processingId,
  difficultyIcons,
  renderRouteMap,
  onEditFieldChange,
  onSaveRouteUpdate,
  onCancelEdit,
  onApproveRoute,
  onStartEditRoute,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  onMakePublic
}) => {
  return (
    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-modal overflow-hidden group hover:border-blue-500/30 transition-all flex flex-col mb-4">
      {renderRouteMap(route)}

      <div className="p-6 space-y-4">
        {route.imageUrls && route.imageUrls.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
            {route.imageUrls.map((url, i) => (
              <div key={i} className="w-20 h-20 rounded-xl overflow-hidden border border-[var(--border-secondary)] shrink-0 relative bg-[var(--bg-surface-alt)]">
                <img src={url} className="w-full h-full object-cover" alt="" />
              </div>
            ))}
          </div>
        )}

        {isEditing ? (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase">Rutenavn</label>
                <input
                  value={editFields.name}
                  onChange={(e) => onEditFieldChange('name', e.target.value)}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 text-sm text-[var(--text-primary)] focus:border-blue-500 outline-none"
                />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase">Vanskelighetsgrad</label>
                <select
                  value={editFields.difficulty}
                  onChange={(e) => onEditFieldChange('difficulty', e.target.value)}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 text-sm text-[var(--text-primary)] focus:border-blue-500 outline-none appearance-none"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                  <option value="Extreme">Extreme</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-3xs font-black text-[var(--text-muted)] uppercase">Beskrivelse</label>
              <textarea
                value={editFields.description}
                onChange={(e) => onEditFieldChange('description', e.target.value)}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 text-sm text-[var(--text-primary)] focus:border-blue-500 outline-none h-24 resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => onSaveRouteUpdate(route.id)} disabled={processingId === route.id} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all">
                {processingId === route.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Lagre endringer
              </button>
              <button onClick={onCancelEdit} className="flex-1 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] py-3 rounded-xl text-2xs font-black uppercase active:scale-95 transition-all">Avbryt</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl shrink-0 ${isPendingMode ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                  {isPendingMode ? <Clock size={20} /> : <Route size={20} />}
                </div>
                <div>
                  <h4 className="text-[var(--text-primary)] font-black uppercase tracking-tight text-sm">{route.name}</h4>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className={`flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border ${route.difficulty === 'Easy' ? 'text-emerald-400 border-emerald-500/30' : route.difficulty === 'Hard' ? 'text-red-400 border-red-500/30' : 'text-blue-400 border-blue-500/30'} uppercase tracking-widest`}>
                      {difficultyIcons[route.difficulty]}
                      {route.difficulty}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-[var(--bg-surface)]" />
                    <span className="text-3xs font-bold text-[var(--text-muted)] uppercase tracking-widest">
                      {route.createdByName || 'Anonym'}
                    </span>
                    {route.region && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-[var(--bg-surface)]" />
                        <span className="text-3xs font-bold text-emerald-500 uppercase tracking-widest">
                          {route.region}
                        </span>
                      </>
                    )}
                    {route.status && route.status !== 'public' && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-[var(--bg-surface)]" />
                        <span className={`flex items-center gap-1 text-4xs font-black uppercase px-1.5 py-0.5 rounded-full ${route.status === 'private' ? 'text-[var(--text-secondary)] bg-[var(--bg-surface-alt)]' : route.status === 'pending' ? 'text-amber-500 bg-amber-500/10' : 'text-red-400 bg-red-500/10'}`}>
                          {route.status === 'private' ? <Lock size={8} /> : <Clock size={8} />}
                          {route.status}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {isPendingMode ? (
                  <>
                    <button
                      onClick={() => onApproveRoute(route, true)}
                      className="p-3 bg-emerald-600/20 text-emerald-500 hover:bg-emerald-600 hover:text-white rounded-xl transition-all active:scale-90"
                      title="Godkjenn og publiser"
                      disabled={processingId === route.id}
                    >
                      <CheckCircle size={18} />
                    </button>
                    <button
                      onClick={() => onApproveRoute(route, false)}
                      className="p-3 bg-red-600/20 text-red-500 hover:bg-red-600 hover:text-white rounded-xl transition-all active:scale-90"
                      title="Avvis"
                      disabled={processingId === route.id}
                    >
                      <X size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    {route.status === 'private' && onMakePublic && (
                      <button
                        onClick={() => onMakePublic(route)}
                        className="p-3 bg-emerald-600/20 text-emerald-500 hover:bg-emerald-600 hover:text-white rounded-xl transition-all active:scale-90"
                        title="Gjør global"
                        disabled={processingId === route.id}
                      >
                        <Globe size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => onStartEditRoute(route)}
                      className="p-3 bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] rounded-xl transition-all active:scale-90"
                      title="Rediger rute-informasjon"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => onRequestDelete(route.id)}
                      className="p-3 bg-red-950/30 text-red-500 hover:bg-red-600 hover:text-white rounded-xl transition-all active:scale-90"
                      title="Slett rute permanent"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="bg-[var(--bg-surface-alt)] p-4 rounded-2xl border border-[var(--border-primary)]">
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed italic line-clamp-3">"{route.description || 'Ingen beskrivelse.'}"</p>
            </div>
            {isDeleting && (
              <div className="bg-red-500/10 border border-red-500/20 p-5 rounded-2xl space-y-4 animate-in zoom-in-95 duration-200">
                <div className="flex items-center gap-3 text-red-500">
                  <AlertTriangle size={20} />
                  <p className="text-tiny font-black uppercase tracking-widest">Slette denne ruten permanent?</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onConfirmDelete(route.id)}
                    disabled={processingId === route.id}
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white py-3 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95"
                  >
                    {processingId === route.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Ja, slett
                  </button>
                  <button
                    onClick={onCancelDelete}
                    className="flex-1 bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] py-3 rounded-xl text-2xs font-black uppercase active:scale-95 transition-all"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRouteCard;
